import { CampaignsTracker, gtm } from '@lessonnine/marketing-tracker';

import makeAjaxRequest from 'src/dataLayer/remoteData/babbelAPI/makeAjaxRequest';
import { registrationErrorHandler } from 'src/dataLayer/remoteData/babbelAPI/createUser/registrationErrorHandler';
import {
  APIGATEWAY_BASE_URL,
  GO_BASE_URL,
  LOCALE
} from 'src/dataLayer/env';
import { trackSuccessfulRegistration } from 'src/dataLayer/remoteData/babbelEventStream';
import { UserType, isUserType } from 'src/viewLayer/components/nodes/InputNode/types';
import type { CreateUserParams, UserRequestPayload } from './types';
import { RequestParams } from '../makeAjaxRequest/types';

const getCreateUserURL = (postVerificationRedirect: string, source: string): string =>
  `${APIGATEWAY_BASE_URL}/v2/${LOCALE}/users?` +
  `immediate_verification_email_advised=true` +
  `&source=${source}` +
  `&post_verification_redirect_url=${encodeURIComponent(postVerificationRedirect)}`;

/*
Returns the created user if a user has been created.
Returns true if the input for the given nodeInputs was valid, but a user could not be created (missing other input)
Returns false if the input for the given nodeInputs was invalid and sets an error to display to the user via the
errorInterface
*/
export default async ({
  postVerificationRedirect,
  email,
  firstname,
  errorInterface,
  nodeInputs,
  password,
  learnLang,
  trackingInfo,
  token,
  cefrLevel,
  invitationToken,
  callback = (): void => {}
}: CreateUserParams): Promise<UserType | boolean> => {
  const requestPayload: UserRequestPayload = {
    user: {
      email,
      firstname,
      password,
      learn_language_alpha3: learnLang,
      cefr_level: cefrLevel,
      privacy_policy: true,
      terms_and_conditions: true
    }

  };

  if (invitationToken) requestPayload.user.invitation_token = invitationToken;
  if (token) requestPayload.user.captcha = { token };

  const requestParams: RequestParams<UserRequestPayload> = {
    verb: 'POST',
    payload: requestPayload,
    url: getCreateUserURL(postVerificationRedirect, trackingInfo.flowId),
  };

  const data = await makeAjaxRequest<UserType, UserRequestPayload, boolean>(
    requestParams,
    (error, params) => registrationErrorHandler(error, {
      ...params,
      nodeInputs,
      errorInterface,
      callback,
      trackingInfo
    })
  );

  // We need to check for the user here because:
  // 'data' will be set to true if there were no issues with the expected input of the current node,
  // (so that we know when to show which error messages on which node ) not necessarily if the user was actually created
  if (isUserType(data)) {
    const campaignsTracker = new CampaignsTracker({ host: GO_BASE_URL });
    campaignsTracker.trackSimpleLead(data.user.uuid);

    gtm.trackSimpleLead({
      leadDate: data.user.registration_date,
      learnLanguageAlpha3: data.user.learn_language_alpha3,
      uuid: data.user.uuid
    });
    trackSuccessfulRegistration({ learnLang, flowId: trackingInfo.flowId, uuid: data.user.uuid });
  }

  callback();
  return data;
};
